import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const FooterContainer = styled.footer`
  background-color: var(--surface);
  padding: 4rem 0 2rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterLogo = styled(Link)`
  font-family: "Space Grotesk", sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  color: var(--text);
  margin-bottom: 1rem;
  display: inline-block;

  span {
    color: var(--primary);
  }
`;

const FooterAbout = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  max-width: 400px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  color: var(--text);
  transition: color 0.3s ease;

  &:hover {
    color: var(--primary);
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: var(--text);
`;

const FooterLink = styled(Link)`
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  transition: color 0.3s ease;

  &:hover {
    color: var(--primary);
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FooterBottomLink = styled(Link)`
  color: var(--text-secondary);
  font-size: 0.9rem;
  transition: color 0.3s ease;

  &:hover {
    color: var(--primary);
  }
`;

function Footer() {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <div>
            <FooterLogo to="/">
              UNINP<span>HACKATHON</span>
            </FooterLogo>
            <FooterAbout>
              Najveći UNINPHACKATHON u regionu koji okuplja talentovane
              programere, dizajnere i inovatore da zajedno stvaraju rešenja za
              budućnost.
            </FooterAbout>
            <SocialLinks>
              <SocialLink
                href="https://www.facebook.com/uninp/?locale=sr_RS"
                target="_blank"
                rel="noopener noreferrer">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SocialLink>
              <SocialLink
                href="https://www.instagram.com/uninphackathon/"
                target="_blank"
                rel="noopener noreferrer">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M17 2H7C4.23858 2 2 4.23858 2 7V17C2 19.7614 4.23858 22 7 22H17C19.7614 22 22 19.7614 22 17V7C22 4.23858 19.7614 2 17 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 11.37C16.1234 12.2022 15.9813 13.0522 15.5938 13.799C15.2063 14.5458 14.5931 15.1514 13.8416 15.5297C13.0901 15.9079 12.2384 16.0396 11.4078 15.9059C10.5771 15.7723 9.80976 15.3801 9.21484 14.7852C8.61991 14.1902 8.22773 13.4229 8.09406 12.5922C7.9604 11.7615 8.09206 10.9099 8.47032 10.1584C8.84858 9.40685 9.45418 8.79374 10.201 8.40624C10.9478 8.01874 11.7978 7.87659 12.63 8C13.4789 8.12588 14.2648 8.52146 14.8717 9.12831C15.4785 9.73515 15.8741 10.5211 16 11.37Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.5 6.5H17.51"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/school/univerzitet-u-novom-pazaru/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16 8C17.5913 8 19.1174 8.63214 20.2426 9.75736C21.3679 10.8826 22 12.4087 22 14V21H18V14C18 13.4696 17.7893 12.9609 17.4142 12.5858C17.0391 12.2107 16.5304 12 16 12C15.4696 12 14.9609 12.2107 14.5858 12.5858C14.2107 12.9609 14 13.4696 14 14V21H10V14C10 12.4087 10.6321 10.8826 11.7574 9.75736C12.8826 8.63214 14.4087 8 16 8Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 9H2V21H6V9Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 6C5.10457 6 6 5.10457 6 4C6 2.89543 5.10457 2 4 2C2.89543 2 2 2.89543 2 4C2 5.10457 2.89543 6 4 6Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SocialLink>
            </SocialLinks>
          </div>

          <FooterColumn>
            <FooterTitle>‎</FooterTitle>
            {/* <FooterLink to="/">Početna</FooterLink>
            <FooterLink to="#o-nama">O Događaju</FooterLink>
            <FooterLink to="#kalendar">Raspored</FooterLink>
            <FooterLink to="#sponzori">Sponzori</FooterLink> */}
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Resursi</FooterTitle>
            <FooterLink
              to="/Uslovi-pravila-takmičenja.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Pravila
            </FooterLink>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Kontakt</FooterTitle>
            <FooterLink to="mailto:uninphackathon@uninp.edu.rs">
              uninphackathon@uninp.edu.rs
            </FooterLink>
            <FooterLink to="tel:+38120316634">+381 20 316-634</FooterLink>
            <FooterLink to="#">Univerzitet u Novom Pazaru</FooterLink>
          </FooterColumn>
        </FooterTop>

        <FooterBottom>
          <Copyright>
            &copy; {new Date().getFullYear()} UNINPHACKATHON. Sva prava
            zadržana.
          </Copyright>

{/*           <FooterBottomLinks>
            <FooterBottomLink to="#">Uslovi korišćenja</FooterBottomLink>
            <FooterBottomLink to="#">Politika privatnosti</FooterBottomLink>
            <FooterBottomLink to="#">Kolačići</FooterBottomLink>
          </FooterBottomLinks> */}
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;
